import { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, MagnifyingGlassIcon, PlusIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { Card, CardBody, Button, Input, Spinner } from '@components/ui';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@components/ui';
import { useCustomers } from '@features/customers/hooks/useCustomers';
import { CustomerFormModal } from './CustomerFormModal';

interface CustomerSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectCustomer: (customer: any) => void;
}

export function CustomerSearchModal({ isOpen, onClose, onSelectCustomer }: CustomerSearchModalProps) {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  // Solo buscar si hay 3 o más caracteres, o si está vacío (para mostrar todos)
  const searchQuery = debouncedSearch.length >= 3 || debouncedSearch.length === 0 ? debouncedSearch : '';

  // Detectar si es búsqueda por RUC o por nombre:
  // - Si contiene números → buscar por RUC (permite letras, guiones, espacios, etc.)
  // - Si solo tiene letras → buscar por nombre
  const hasNumbers = /\d/.test(searchQuery);

  const { data, isLoading } = useCustomers({
    page: currentPage,
    limit: 10,
    name: hasNumbers ? undefined : searchQuery,
    taxId: hasNumbers ? searchQuery : undefined,
  });

  // Debounce: esperar 1 segundo después de que el usuario deje de escribir
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 1000);

    return () => clearTimeout(timer);
  }, [search]);

  // Reset page to 1 when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);

  // El backend ya devuelve los datos paginados, usar directamente
  const limit = data?.limit || 10;
  const totalPages = data?.totalPages || 1;
  const paginatedCustomers = data?.customers || [];
  const startIndex = (currentPage - 1) * limit;
  const endIndex = startIndex + limit;

  // Debug: Log data and pagination state
  useEffect(() => {
    console.log('📊 CustomerSearchModal - Estado de paginación:', {
      currentPage,
      search,
      total: data?.total,
      totalPages,
      backendTotalPages: data?.totalPages,
      customersLength: data?.customers?.length,
      paginatedCustomersLength: paginatedCustomers.length,
    });
  }, [currentPage, search, data, totalPages, paginatedCustomers.length]);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-2xl bg-white dark:bg-secondary-800 p-4 sm:p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title
                  as="h3"
                  className="text-lg font-medium leading-6 text-secondary-900 dark:text-white flex items-center justify-between"
                >
                  <span>Administrador de clientes</span>
                  <button
                    onClick={onClose}
                    className="text-secondary-400 hover:text-secondary-600 dark:hover:text-secondary-200"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </Dialog.Title>

                <div className="mt-4 space-y-4">
                  {/* Search Bar */}
                  <Card>
                    <CardBody className="p-4">
                      <div className="space-y-2">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                          <div className="flex flex-1 items-center gap-3">
                            <MagnifyingGlassIcon className="w-5 h-5 shrink-0 text-secondary-400" />
                            <Input
                              type="text"
                              placeholder="Buscar por nombre o RUC (mínimo 3 caracteres)..."
                              value={search}
                              onChange={(e) => setSearch(e.target.value)}
                              className="flex-1"
                            />
                          </div>
                          <Button
                            variant="primary"
                            onClick={() => setIsCreateModalOpen(true)}
                            className="flex w-full items-center justify-center gap-2 whitespace-nowrap sm:w-auto"
                          >
                            <PlusIcon className="w-5 h-5" />
                            Nuevo Cliente
                          </Button>
                        </div>
                        {search.length > 0 && search.length < 3 && (
                          <p className="text-xs text-secondary-500 dark:text-secondary-400 ml-8">
                            Escribe al menos 3 caracteres para buscar...
                          </p>
                        )}
                        {search !== debouncedSearch && search.length >= 3 && (
                          <p className="text-xs text-secondary-500 dark:text-secondary-400 ml-8">
                            Buscando...
                          </p>
                        )}
                      </div>
                    </CardBody>
                  </Card>

                  {/* Results Table */}
                  <Card>
                    <CardBody className="p-0">
                      {isLoading ? (
                        <div className="flex items-center justify-center py-12">
                          <Spinner size="md" />
                        </div>
                      ) : paginatedCustomers.length === 0 ? (
                        <div className="text-center py-12">
                          <p className="text-sm text-secondary-500 dark:text-secondary-400">
                            No se encontraron clientes
                          </p>
                        </div>
                      ) : (
                        <>
                          {/* Móvil: lista de tarjetas; tocar toda la fila selecciona. */}
                          <ul className="divide-y divide-secondary-200 dark:divide-secondary-700 sm:hidden">
                            {paginatedCustomers.map((customer) => (
                              <li key={customer.id}>
                                <button
                                  type="button"
                                  onClick={() => onSelectCustomer(customer)}
                                  className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-secondary-50 dark:hover:bg-secondary-700"
                                >
                                  <div className="min-w-0">
                                    <p className="truncate font-medium text-secondary-900 dark:text-white">{customer.name}</p>
                                    <p className="mt-0.5 truncate text-xs text-secondary-500 dark:text-secondary-400">
                                      {customer.taxId}-{customer.taxIdSub || ''} · {customer.taxIdType}
                                    </p>
                                  </div>
                                  <ChevronRightIcon className="h-5 w-5 shrink-0 text-primary-600" />
                                </button>
                              </li>
                            ))}
                          </ul>

                          {/* Escritorio: tabla completa. */}
                          <div className="hidden sm:block">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Nombre</TableHead>
                                  <TableHead>RUC/ID</TableHead>
                                  <TableHead>Tipo</TableHead>
                                  <TableHead className="text-right">Acción</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {paginatedCustomers.map((customer) => (
                                  <TableRow key={customer.id}>
                                    <TableCell className="font-medium">{customer.name}</TableCell>
                                    <TableCell>
                                      {customer.taxId}-{customer.taxIdSub || ''}
                                    </TableCell>
                                    <TableCell className="text-secondary-600 dark:text-secondary-400 text-xs">
                                      {customer.taxIdType}
                                    </TableCell>
                                    <TableCell className="text-right">
                                      <Button
                                        variant="primary"
                                        size="sm"
                                        onClick={() => onSelectCustomer(customer)}
                                      >
                                        Seleccionar
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>

                          {/* Pagination Controls */}
                          {data && totalPages > 1 && (
                            <div className="flex flex-col items-center gap-3 border-t border-secondary-200 dark:border-secondary-700 px-4 py-4 sm:flex-row sm:justify-between sm:px-6">
                              <div className="text-center text-sm text-secondary-600 dark:text-secondary-400 sm:text-left">
                                Mostrando {startIndex + 1} a {Math.min(endIndex, data.total)} de {data.total} clientes
                              </div>
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                  disabled={currentPage === 1}
                                  className="flex items-center gap-1"
                                >
                                  <ChevronLeftIcon className="w-4 h-4" />
                                  Anterior
                                </Button>
                                <div className="text-sm text-secondary-600 dark:text-secondary-400">
                                  Página {currentPage} de {totalPages}
                                </div>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                  disabled={currentPage === totalPages}
                                  className="flex items-center gap-1"
                                >
                                  Siguiente
                                  <ChevronRightIcon className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </CardBody>
                  </Card>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>

        {/* Customer Form Modal */}
        <CustomerFormModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onCustomerCreated={(customer) => {
            onSelectCustomer(customer);
            setIsCreateModalOpen(false);
          }}
        />
      </Dialog>
    </Transition>
  );
}
